import { FC } from "react";
import { useTranslation } from "react-i18next";
import { UploadError, ErrorAction } from "./uploadErrorHelper";
import "./UploadErrorDisplay.scss";

interface UploadErrorDisplayProps {
  error: UploadError;
  onDismiss: () => void;
  onRetry?: () => void;
  onClearFile?: () => void;
  onTryDifferentFile?: () => void;
  onRefreshPage?: () => void;
  onContactSupport?: () => void;
}

const UploadErrorDisplay: FC<UploadErrorDisplayProps> = ({
  error,
  onDismiss,
  onRetry,
  onClearFile,
  onTryDifferentFile,
  onRefreshPage,
  onContactSupport,
}) => {
  const { t } = useTranslation();

  const handleAction = (action: ErrorAction) => {
    switch (action) {
      case ErrorAction.RETRY:
        onRetry?.();
        break;
      case ErrorAction.CLEAR_FILE:
        onClearFile?.();
        onDismiss();
        break;
      case ErrorAction.TRY_DIFFERENT_FILE:
        onTryDifferentFile?.();
        onDismiss();
        break;
      case ErrorAction.REFRESH_PAGE:
        onRefreshPage?.();
        break;
      case ErrorAction.CONTACT_SUPPORT:
        onContactSupport?.();
        break;
      default:
        break;
    }
  };

  const getActionLabel = (action: ErrorAction): string => {
    switch (action) {
      case ErrorAction.RETRY:
        return t("error.action.retry");
      case ErrorAction.CLEAR_FILE:
        return t("error.action.clearFile");
      case ErrorAction.TRY_DIFFERENT_FILE:
        return t("error.action.tryDifferentFile");
      case ErrorAction.REFRESH_PAGE:
        return t("error.action.refreshPage");
      case ErrorAction.CONTACT_SUPPORT:
        return t("error.action.contactSupport");
      default:
        return "";
    }
  };

  return (
    <div
      className={`upload-error-display upload-error-display--${error.severity.toLowerCase()}`}
    >
      <div className="upload-error-display__header">
        <span className="upload-error-display__icon">{error.icon}</span>
        <h4 className="upload-error-display__title">{t(error.title)}</h4>
        <button
          className="upload-error-display__close"
          type="button"
          onClick={onDismiss}
          aria-label={t("Close")}
        >
          ×
        </button>
      </div>

      <div className="upload-error-display__content">
        <p className="upload-error-display__message">{t(error.message)}</p>

        {error.suggestions && error.suggestions.length > 0 && (
          <div className="upload-error-display__suggestions">
            <strong>{t("error.whatToDo")}:</strong>
            <ul>
              {error.suggestions.map((suggestion) => (
                <li key={suggestion}>{t(suggestion)}</li>
              ))}
            </ul>
          </div>
        )}

            {error.actions && error.actions.length > 0 && (
              <div className="upload-error-display__actions">
                {error.actions.map((action) => (
                  <button
                    key={action}
                    className={`btn ${
                      action === ErrorAction.RETRY ? "btn-primary" : "btn-default"
                    }`}
                    type="button"
                    onClick={() => handleAction(action)}
                  >
                    {getActionLabel(action)}
                  </button>
                ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadErrorDisplay;
